import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getIcon } from "@/lib/iconMapping";
import { SEOHead } from "@/components/SEOHead";

interface FeatureDetail {
  id: string;
  detail_text: string;
  display_order: number;
}

interface Feature {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  display_order: number;
  plan_ids: string[];
  feature_details: FeatureDetail[];
}

interface FeatureCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  display_order: number;
  features: Feature[];
}

interface ComparisonFeature {
  id: string;
  name: string;
  basic: boolean;
  standard: boolean;
  display_order: number;
}

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  display_order: number;
}

const PLAN_BADGE_STYLES: Record<string, string> = {
  basic: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  standard: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  trial: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800",
};

const getPlanBadgeStyles = (name: string) =>
  PLAN_BADGE_STYLES[name.toLowerCase()] ?? "bg-muted text-foreground border-border";

export default function Features() {
  const [planFilter, setPlanFilter] = useState<string>('all');

  // Pricing plans (drives the filter tabs and the badges)
  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['pricing-plans-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pricing_plans')
        .select('id, name, price, display_order')
        .order('display_order');
      if (error) throw error;
      return data as PricingPlan[];
    },
  });

  // Fetch feature categories with features, details, and plan links
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['feature-categories'],
    queryFn: async () => {
      const [{ data: categoriesData, error: categoriesError },
             { data: featuresData, error: featuresError },
             { data: linksData, error: linksError }] = await Promise.all([
        supabase.from('feature_categories').select('*').order('display_order'),
        supabase.from('features').select('*, feature_details(*)').order('display_order'),
        supabase.from('feature_plans').select('feature_id, plan_id'),
      ]);

      if (categoriesError) throw categoriesError;
      if (featuresError) throw featuresError;
      if (linksError) throw linksError;

      const planIdsByFeature: Record<string, string[]> = {};
      (linksData || []).forEach((l: any) => {
        (planIdsByFeature[l.feature_id] ||= []).push(l.plan_id);
      });

      // Group features by category
      const categoriesWithFeatures: FeatureCategory[] = (categoriesData || []).map((category: any) => ({
        ...category,
        features: (featuresData || [])
          .filter((f: any) => f.category_id === category.id)
          .map((f: any) => ({
            ...f,
            plan_ids: planIdsByFeature[f.id] || [],
            feature_details: (f.feature_details || []).sort(
              (a: FeatureDetail, b: FeatureDetail) => a.display_order - b.display_order
            ),
          })),
      }));

      return categoriesWithFeatures;
    },
  });

  // Fetch comparison features
  const { data: comparisonFeatures = [], isLoading: comparisonLoading } = useQuery({
    queryKey: ['comparison-features'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comparison_features')
        .select('*')
        .order('display_order');

      if (error) throw error;
      return data as ComparisonFeature[];
    }
  });

  const filteredCategories = categories.map(category => ({
    ...category,
    features: category.features.filter(feature => {
      if (planFilter === 'all') return true;
      return feature.plan_ids.includes(planFilter);
    })
  })).filter(category => category.features.length > 0);

  const filteredFeatureCount = filteredCategories.reduce((acc, c) => acc + c.features.length, 0);
  const activePlan = plans.find(p => p.id === planFilter);

  if (categoriesLoading || comparisonLoading || plansLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 space-y-16 animate-fade-in pt-12">
      <SEOHead slug="features" />
      <header className="text-center max-w-3xl mx-auto space-y-4">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground">
          PTA Hub Features
        </h1>
        <p className="text-lg text-muted-foreground">
          Every tool your PTA needs — member management, events, fundraising, finances and more — across all our plans.
        </p>
      </header>
      {/* Plan Filter Section */}
      <section className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-6">Filter by Plan</h2>
        <Tabs
          value={planFilter}
          onValueChange={(v) => setPlanFilter(v)}
          className="w-full max-w-3xl mx-auto"
        >
          <TabsList
            className="grid w-full"
            style={{ gridTemplateColumns: `repeat(${plans.length + 1}, minmax(0, 1fr))` }}
          >
            <TabsTrigger value="all" className="gap-2">All Features</TabsTrigger>
            {plans.map(plan => (
              <TabsTrigger key={plan.id} value={plan.id} className="gap-2">
                <span className="hidden sm:inline">{plan.name}</span>
                <Badge variant="outline" className={`ml-1 border-0 text-xs ${getPlanBadgeStyles(plan.name)}`}>
                  £{Number(plan.price).toFixed(0)}/yr
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <p className="text-sm text-muted-foreground mt-4">
          {planFilter === 'all'
            ? `Showing all ${filteredFeatureCount} features across every plan`
            : `Showing ${filteredFeatureCount} feature${filteredFeatureCount === 1 ? '' : 's'} included in ${activePlan?.name ?? 'this'} Plan`}
        </p>
      </section>

      {/* Feature Categories */}
      <section className="space-y-12">
        {filteredCategories.map((category) => {
          const CategoryIcon = getIcon(category.icon);
          return (
            <div key={category.id} className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <CategoryIcon className="w-6 h-6 text-black dark:text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{category.name}</h2>
                  <p className="text-muted-foreground">{category.description}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.features.map((feature) => {
                  const FeatureIcon = getIcon(feature.icon);
                  const linkedPlans = plans.filter(p => feature.plan_ids.includes(p.id));
                  return (
                    <Card key={feature.id} className="group hover:shadow-elevated transition-all duration-300 border-border/50 hover:border-primary/30">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                              <FeatureIcon className="w-5 h-5 text-black dark:text-white transition-colors" />
                            </div>
                            <CardTitle className="text-lg">{feature.name}</CardTitle>
                          </div>
                          <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                            {linkedPlans.map(p => (
                              <Badge key={p.id} variant="outline" className={`text-xs ${getPlanBadgeStyles(p.name)}`}>
                                {p.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-3">{feature.description}</CardDescription>
                        {feature.feature_details.length > 0 && (
                          <Accordion type="single" collapsible className="w-full">
                            <AccordionItem value="details" className="border-0">
                              <AccordionTrigger className="text-sm text-primary hover:no-underline py-2">
                                Learn more about {feature.name}
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className="space-y-1.5 text-sm text-muted-foreground">
                                  {feature.feature_details.map((detail) => (
                                    <li key={detail.id} className="flex items-start gap-2">
                                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                      {detail.detail_text}
                                    </li>
                                  ))}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* Comparison Table */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-foreground mb-2">Compare Plans</h2>
          <p className="text-muted-foreground">See exactly what's included in each plan</p>
        </div>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[300px] font-semibold">Feature</TableHead>
                  <TableHead className="text-center w-[150px]">
                    <div className="flex flex-col items-center gap-1 pb-2">
                      <span className="font-semibold">Basic</span>
                      <Badge variant="outline" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-0">
                        £99.99/year
                      </Badge>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[150px]">
                    <div className="flex flex-col items-center gap-1 pb-2">
                      <span className="font-semibold">Standard</span>
                      <Badge variant="outline" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-0">
                        £129.99/year
                      </Badge>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow key={feature.id} className={index % 2 === 0 ? 'bg-muted/20' : ''}>
                    <TableCell className="font-medium">{feature.name}</TableCell>
                    <TableCell className="text-center">
                      {feature.basic ? (
                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {feature.standard ? (
                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </section>

      {/* Bottom padding */}
      <div className="pb-16" />

    </div>
  );
}
